export interface DeviceInfo {
  userAgent: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  deviceModel: string;
  os: string | null;
  browser: string | null;
}

/**
 * Stateless helper for extracting device and browser information from the
 * browser's user agent string. Used to enrich analytics event payloads.
 */
export class DeviceInfoHelper {

  static getDeviceInfo(): DeviceInfo {
    const ua = navigator.userAgent;
    return {
      userAgent: ua,
      deviceType: DeviceInfoHelper.parseDeviceType(ua),
      deviceModel: DeviceInfoHelper.parseDeviceModel(ua),
      os: DeviceInfoHelper.parseOS(ua),
      browser: DeviceInfoHelper.parseBrowser(ua),
    };
  }

  static parseDeviceType(ua: string): 'mobile' | 'tablet' | 'desktop' {
    if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      return 'mobile';
    }
    if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) {
      return 'tablet';
    }
    return 'desktop';
  }

  static parseDeviceModel(ua: string): string {
    // iPhone — Apple does not include model number in the UA, just "iPhone"
    if (/iPhone/i.test(ua)) return 'iPhone';

    // iPad
    if (/iPad/i.test(ua)) return 'iPad';

    // Android devices — model sits between the semicolons: "Linux; Android 15; Pixel 9"
    const androidMatch = ua.match(/Android[^;]*;\s*([^)]+)\)/);
    if (androidMatch) return androidMatch[1].trim();

    // Unrecognised — return the raw UA for visibility
    return `Other: ${ua}`;
  }

  static parseOS(ua: string): string {
    if (/Windows NT 10\.0/i.test(ua)) return 'Windows 10/11';
    if (/Windows NT 6\.3/i.test(ua)) return 'Windows 8.1';
    if (/Windows NT 6\.1/i.test(ua)) return 'Windows 7';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Android (\d+[.\d]*)/i.test(ua)) {
      const m = ua.match(/Android (\d+[.\d]*)/i);
      return m ? `Android ${m[1]}` : 'Android';
    }
    if (/iPhone OS ([\d_]+)/i.test(ua)) {
      const m = ua.match(/iPhone OS ([\d_]+)/i);
      return m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
    }
    if (/iPad.*OS ([\d_]+)/i.test(ua)) {
      const m = ua.match(/OS ([\d_]+)/i);
      return m ? `iPadOS ${m[1].replace(/_/g, '.')}` : 'iPadOS';
    }
    if (/Mac OS X ([\d_]+)/i.test(ua)) {
      const m = ua.match(/Mac OS X ([\d_]+)/i);
      return m ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
    }
    if (/Linux/i.test(ua)) return 'Linux';

    // Unrecognised — return the raw UA for visibility
    return `Other: ${ua}`;
  }

  static parseBrowser(ua: string): string {
    // Order matters — check more specific patterns first
    if (/Edg\/([\d.]+)/i.test(ua)) {
      const m = ua.match(/Edg\/([\d.]+)/i);
      return m ? `Edge ${m[1]}` : 'Edge';
    }
    if (/OPR\/([\d.]+)/i.test(ua)) {
      const m = ua.match(/OPR\/([\d.]+)/i);
      return m ? `Opera ${m[1]}` : 'Opera';
    }
    if (/Firefox\/([\d.]+)/i.test(ua)) {
      const m = ua.match(/Firefox\/([\d.]+)/i);
      return m ? `Firefox ${m[1]}` : 'Firefox';
    }
    if (/Chrome\/([\d.]+)/i.test(ua)) {
      const m = ua.match(/Chrome\/([\d.]+)/i);
      return m ? `Chrome ${m[1]}` : 'Chrome';
    }
    if (/Safari\/([\d.]+)/i.test(ua) && /Version\/([\d.]+)/i.test(ua)) {
      const m = ua.match(/Version\/([\d.]+)/i);
      return m ? `Safari ${m[1]}` : 'Safari';
    }

    // Unrecognised — return the raw UA for visibility
    return `Other: ${ua}`;
  }
}


